import type { CmmReadinessFacts } from '../../../contracts/cmm-gateway.js';
import {
  viewerInternalCodeSql,
  viewerProfileJoin,
} from './counterparty-code-sql.js';
import type { DbClient } from './types.js';

/** What the CMM readiness checks read from a style. */
export interface StyleCmmFacts {
  claimIntentions: string[];
  /** Top-level lines of the main material, summed. `null` when there are none. */
  materialCompositionSum: number | null;
}

export interface UnsentCmmLineRow {
  id: string;
  orderId: string;
  orderLineNumber: string | null;
  styleId: string | null;
  quantity: number | null;
  unit: string | null;
}

/** `styles.claim_intentions` is a comma-separated list. */
export function splitClaimIntentions(value: string | null): string[] {
  return (value ?? '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

const overridden = (alias: string) =>
  `(${alias}.overridden_flag IS NOT NULL AND POSITION(',claim_intentions,' IN ',' || ${alias}.overridden_flag || ',') > 0)`;

/**
 * A variant or SKU inherits its parent's claim intentions unless it overrides
 * them, the same rule the styles screens apply (`inheritFieldVariant` /
 * `inheritFieldSku`). Reading the line's own row would report a variant with
 * inherited claims as having none.
 */
const EFFECTIVE_CLAIM_INTENTIONS = `CASE s.product_level
    WHEN 'variant' THEN CASE WHEN ${overridden('s')} THEN s.claim_intentions
                             ELSE COALESCE(p.claim_intentions, s.claim_intentions) END
    WHEN 'sku' THEN CASE WHEN ${overridden('s')} THEN s.claim_intentions
                         ELSE COALESCE(CASE WHEN ${overridden('p')} THEN p.claim_intentions
                                            ELSE COALESCE(gp.claim_intentions, p.claim_intentions) END,
                                       s.claim_intentions) END
    ELSE s.claim_intentions END`;

/**
 * Claim intentions and material composition per style, for one page of rows.
 *
 * Composition sums only the main material (`is_main_material = 1`; a re-import
 * demotes the previous header rather than deleting it) and only its top-level
 * lines (`parent_id IS NULL`): each level of the material tree is its own
 * 100%, so a child line is a share of its parent, not of the garment.
 */
export async function getStyleCmmFactsDb(
  db: DbClient,
  styleIds: string[],
): Promise<Map<string, StyleCmmFacts>> {
  if (styleIds.length === 0) return new Map();

  const inClause = db.whereInPlaceholders({
    field: 's.id',
    listIds: styleIds,
    bindingName: 'styleId',
  });
  const { rows } = await db.select<{
    id: string;
    claimIntentions: string | null;
    materialCompositionSum: number | null;
  }>(
    `SELECT s.id,
            ${EFFECTIVE_CLAIM_INTENTIONS} AS claim_intentions,
            (SELECT SUM(sml.percentage)::float8
               FROM style_material_links smk
               JOIN style_material_lines sml
                 ON sml.material_header_id = smk.material_header_id
                AND sml.parent_id IS NULL
              WHERE smk.style_id = s.id
                AND smk.is_main_material = 1) AS material_composition_sum
       FROM styles s
       LEFT JOIN styles p ON p.id = s.parent_id
       LEFT JOIN styles gp ON gp.id = p.parent_id
      WHERE ${inClause}`,
    db.whereInArrayBindings(styleIds, 'styleId'),
  );

  return new Map(
    rows.map(row => [
      row.id,
      {
        claimIntentions: splitClaimIntentions(row.claimIntentions),
        materialCompositionSum:
          row.materialCompositionSum == null
            ? null
            : Number(row.materialCompositionSum),
      },
    ]),
  );
}

interface CmmFactsOrderRow {
  id: string;
  orderNumber: string | null;
  orderType: string;
  isArchived: boolean | number;
  buyerCompanyId: string | null;
  supplierCompanyId: string | null;
  orderDate: Date | string | null;
  agreedDeliveryDate: Date | string | null;
}

const toDateOnly = (value: Date | string | null): string | null => {
  if (value == null) return null;
  return value instanceof Date ? value.toISOString().slice(0, 10) : value;
};

/**
 * Everything `computeCmmReadiness` reads, for each of these orders, from the
 * Order Hub's own tables — the same queries the lists use, so the send and the
 * readiness endpoint apply one rule. Orders that are not the caller's are
 * absent from the map. A fixed number of queries, whatever the page size.
 *
 * `tenant.cmmEnabled` is always true: no tenant configuration table exists yet,
 * and the contract reads a missing row as "not configured", never "disabled".
 * The main-raw-material fields stay null because no check reads them.
 */
export async function loadCmmReadinessFactsForOrdersDb(
  db: DbClient,
  params: { companyId: string; orderIds: string[] },
): Promise<Map<string, CmmReadinessFacts>> {
  if (params.orderIds.length === 0) return new Map();

  const orderIn = db.whereInPlaceholders({
    field: 'id',
    listIds: params.orderIds,
    bindingName: 'orderId',
  });
  const { rows: orders } = await db.select<CmmFactsOrderRow>(
    `SELECT id, order_number, order_type, is_archived,
            buyer_company_id, supplier_company_id, order_date, agreed_delivery_date
       FROM orders
      WHERE ${orderIn} AND for_company_id = :companyId`,
    {
      ...db.whereInArrayBindings(params.orderIds, 'orderId'),
      companyId: params.companyId,
    },
  );
  if (orders.length === 0) return new Map();

  const ownIds = orders.map(order => order.id);
  const lineIn = db.whereInPlaceholders({
    field: 'order_id',
    listIds: ownIds,
    bindingName: 'orderId',
  });
  const { rows: lines } = await db.select<UnsentCmmLineRow>(
    `SELECT id, order_id, order_line_number, style_id,
            quantity::float8 AS quantity, unit
       FROM order_lines
      WHERE ${lineIn}
      ORDER BY line_no ASC, id ASC`,
    db.whereInArrayBindings(ownIds, 'orderId'),
  );
  const styles = await getStyleCmmFactsDb(db, [
    ...new Set(
      lines.map(line => line.styleId).filter((id): id is string => !!id),
    ),
  ]);
  const profiles = await getSupplierProfilesDb(db, params.companyId, [
    ...new Set(
      orders
        .map(order => order.supplierCompanyId)
        .filter((id): id is string => !!id),
    ),
  ]);

  return new Map(
    orders.map(order => {
      const seller = order.supplierCompanyId
        ? profiles.get(order.supplierCompanyId)
        : undefined;
      const facts: CmmReadinessFacts = {
        order: {
          orderId: order.id,
          orderNumber: order.orderNumber,
          orderType: order.orderType,
          isArchived: Boolean(order.isArchived),
          buyerCompanyId: order.buyerCompanyId,
          supplierCompanyId: order.supplierCompanyId,
          orderDate: toDateOnly(order.orderDate),
          agreedDeliveryDate: toDateOnly(order.agreedDeliveryDate),
        },
        lines: lines
          .filter(line => line.orderId === order.id)
          .map(line => {
            const style = styles.get(line.styleId ?? '');
            return {
              orderLineId: line.id,
              orderLineNumber: line.orderLineNumber,
              styleId: line.styleId,
              quantity: line.quantity,
              unit: line.unit,
              claimIntentions: style?.claimIntentions ?? [],
              mainRawMaterial: null,
              mainRawMaterialPct: null,
              materialCompositionSum: style?.materialCompositionSum ?? null,
            };
          }),
        // No supplier means nothing to read: resolved, with nothing found. A
        // supplier with no `companies_view` row is unresolved, not codeless.
        seller: order.supplierCompanyId
          ? {
              officialName: seller?.officialName ?? null,
              internalCompanyCode: seller?.internalCompanyCode ?? null,
              resolved: seller !== undefined,
            }
          : { officialName: null, internalCompanyCode: null, resolved: true },
        tenant: { cmmEnabled: true },
      };
      return [order.id, facts];
    }),
  );
}

/** One order's facts; `null` when the order is not the caller's. */
export async function loadCmmReadinessFactsDb(
  db: DbClient,
  params: { companyId: string; orderId: string },
): Promise<CmmReadinessFacts | null> {
  const byOrderId = await loadCmmReadinessFactsForOrdersDb(db, {
    companyId: params.companyId,
    orderIds: [params.orderId],
  });
  return byOrderId.get(params.orderId) ?? null;
}

/** The internal code is the viewer's own code for the supplier — see
 *  `viewerInternalCodeSql`. */
async function getSupplierProfilesDb(
  db: DbClient,
  viewerCompanyId: string,
  companyIds: string[],
): Promise<
  Map<
    string,
    { officialName: string | null; internalCompanyCode: string | null }
  >
> {
  if (companyIds.length === 0) return new Map();

  const inClause = db.whereInPlaceholders({
    field: 'cv.id',
    listIds: companyIds,
    bindingName: 'companyId',
  });
  const { rows } = await db.select<{
    id: string;
    officialName: string | null;
    internalCompanyCode: string | null;
  }>(
    `SELECT cv.id,
            cv.official_name,
            ${viewerInternalCodeSql('viewer_profile', 'cv')} AS internal_company_code
       FROM companies_view cv
       ${viewerProfileJoin('viewer_profile', 'cv.id', ':viewerCompanyId')}
      WHERE ${inClause}`,
    {
      ...db.whereInArrayBindings(companyIds, 'companyId'),
      viewerCompanyId,
    },
  );
  return new Map(
    rows.map(row => [
      row.id,
      {
        officialName: row.officialName ?? null,
        internalCompanyCode: row.internalCompanyCode ?? null,
      },
    ]),
  );
}
